import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { billsRead } from "@/lib/billsNative";

const EFFECT_TONE = {
  INCLUDE: "text-[#2f5d3a]",
  EXCLUDE: "text-archival-secondary",
  HOLD_FOR_DECISION: "text-amber-800",
};

const fmtDay = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const Editor = ({ c, busy, onSave, onCancel }) => {
  const [state, setState] = useState(c.state || "REVIEW");
  const [effect, setEffect] = useState(c.landed_cost_effect || "INCLUDE");
  const [scope, setScope] = useState("OUTLET");
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [beneficiary, setBeneficiary] = useState("");
  const other = state === "BENEFICIARY_OTHER_ENTITY";
  return (
    <div className="mt-3 space-y-3 border-t border-[#E4E0D8] pt-3" data-testid={`policy-editor-${c.component_kind}`}>
      <p className="text-[13px] text-archival-secondary">How should this component affect landed cost for this outlet?</p>
      <div className="flex flex-wrap gap-2">
        {c.allowed_states.map((s) => (
          <button key={s} type="button" onClick={() => setState(s)} data-testid={`policy-state-${c.component_kind}-${s}`}
            className={`min-h-[40px] px-3 rounded-full border text-[13px] transition-colors ${state === s ? "border-[#2f5d3a] bg-[#eef3ec] text-[#2f5d3a] font-medium" : "border-[#E4E0D8] bg-white text-archival-secondary"}`}>
            {s === "RECOVERABLE" ? "Recoverable" : s === "NON_RECOVERABLE" ? "Non-recoverable" : s === "REVIEW" ? "Review"
              : s === "RECOVERABLE_BY_THIS_ENTITY" ? "Recoverable by this entity"
              : s === "NOT_RECOVERABLE_BY_THIS_ENTITY" ? "Not recoverable by this entity"
              : s === "BENEFICIARY_OTHER_ENTITY" ? "Credit belongs to another entity"
              : s === "INCLUDE_IN_LANDED_COST" ? "Include" : "Exclude"}
          </button>
        ))}
      </div>
      {other && (
        <div className="space-y-2" data-testid={`policy-beneficiary-${c.component_kind}`}>
          <div className="flex gap-2">
            {["INCLUDE", "EXCLUDE"].map((e) => (
              <button key={e} type="button" onClick={() => setEffect(e)} data-testid={`policy-effect-${c.component_kind}-${e}`}
                className={`min-h-[40px] px-3 rounded-md border text-[13px] ${effect === e ? "border-[#2f5d3a] bg-[#eef3ec] text-[#2f5d3a] font-medium" : "border-[#E4E0D8] bg-white text-archival-secondary"}`}>
                {e === "INCLUDE" ? "This outlet bears the cost" : "This outlet does not bear the cost"}
              </button>
            ))}
          </div>
          <input value={beneficiary} onChange={(e) => setBeneficiary(e.target.value)} placeholder="Who receives the credit? (e.g. landlord)"
            data-testid={`policy-beneficiary-ref-${c.component_kind}`} className="w-full h-11 px-3 rounded-md border border-[#E4E0D8] text-[14px]" />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {["OUTLET", "ORGANISATION"].map((s) => (
          <button key={s} type="button" onClick={() => setScope(s)} data-testid={`policy-scope-${c.component_kind}-${s}`}
            className={`min-h-[40px] px-3 rounded-md border text-[13px] ${scope === s ? "border-[#2f5d3a] bg-[#eef3ec] text-[#2f5d3a] font-medium" : "border-[#E4E0D8] bg-white text-archival-secondary"}`}>
            {s === "OUTLET" ? "This outlet only" : "Organisation default"}
          </button>
        ))}
      </div>
      <label className="block text-[13px] text-archival-secondary">Effective from
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid={`policy-from-${c.component_kind}`}
          className="mt-1 w-full h-11 px-3 rounded-md border border-[#E4E0D8] text-[14px]" />
      </label>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why (kept in the audit trail)"
        data-testid={`policy-reason-${c.component_kind}`} className="w-full h-11 px-3 rounded-md border border-[#E4E0D8] text-[14px]" />
      <div className="flex gap-2">
        <button type="button" disabled={busy} data-testid={`policy-save-${c.component_kind}`}
          onClick={() => onSave({ component_kind: c.component_kind, state, landed_cost_effect: other ? effect : null, beneficiary_type: other ? "OTHER_ENTITY" : null, beneficiary_ref: other ? beneficiary || null : null, effective_from: from, reason: reason || null, scope })}
          className="h-11 px-5 rounded-md text-white text-[14px] font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--action)" }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : "Save policy"}
        </button>
        <button type="button" onClick={onCancel} className="h-11 px-4 rounded-md border border-[#E4E0D8] text-[14px]">Cancel</button>
      </div>
    </div>
  );
};

export default function LandedCostPolicy() {
  const [st, setSt] = useState({ phase: "loading", d: null });
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = () => billsRead.landedCostPolicy().then((r) =>
    setSt(r.ok ? { phase: "ready", d: r.data } : { phase: r.status === 403 ? "denied" : "unavailable", d: null }));
  useEffect(() => { load(); }, []);

  const save = async (body) => {
    setBusy(true); setErr(null);
    const r = await billsRead.setLandedCostPolicy(body);
    setBusy(false);
    if (r.ok) { setSt({ phase: "ready", d: r.data }); return setOpen(null); }
    setErr(r.data?.message || "That policy didn't save. Please check the values and try again.");
  };

  const d = st.d;
  return (
    <div className="min-h-screen bg-archival-base" data-testid="landed-cost-policy-page">
      <div className="max-w-[760px] mx-auto px-4 pb-16">
        <Link to="/more" className="inline-flex items-center gap-1 min-h-[44px] text-[14px] text-archival-secondary" data-testid="policy-back"><ArrowLeft size={16} />More</Link>
        <h1 className="text-[26px] font-semibold text-archival-primary">Tax &amp; landed cost policy</h1>
        <p className="mt-1 text-[14px] text-archival-secondary max-w-[60ch]">
          Decide how each invoice component affects the cost of the ingredients you receive at this outlet.
          This is your business policy — 1OS does not determine tax recoverability for you.
        </p>
        {st.phase === "loading" && <p className="mt-6 flex items-center gap-2 text-archival-secondary" data-testid="policy-loading"><Loader2 size={16} className="animate-spin" />Loading policy…</p>}
        {st.phase === "denied" && <p className="mt-6 text-[15px]" data-testid="policy-denied">Only an owner or administrator can view this policy.</p>}
        {st.phase === "unavailable" && <p role="alert" className="mt-6 text-[15px]" data-testid="policy-unavailable">Policy is temporarily unavailable. Please try again shortly.</p>}
        {d && (
          <>
            {!d.can_edit && <p className="mt-4 text-[13px] rounded-md bg-archival-soft px-3 py-2" data-testid="policy-readonly">View only — an owner or administrator can change these.</p>}
            {err && <p role="alert" className="mt-4 text-[14px] rounded-md border border-red-300 bg-red-50 px-3 py-2 text-red-900" data-testid="policy-error">{err}</p>}
            <ul className="mt-5 space-y-2" data-testid="policy-list">
              {d.components.map((c) => (
                <li key={c.component_kind} className="rounded-md border border-[#E4E0D8] bg-white px-4 py-3" data-testid={`policy-row-${c.component_kind}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-[15px] font-semibold text-archival-primary capitalize">{c.component_label}</p>
                      <p className={`text-[13px] ${EFFECT_TONE[c.effect]}`} data-testid={`policy-effect-text-${c.component_kind}`}>
                        {c.state_label} · {c.effect_label}
                      </p>
                      <p className="text-[12px] text-archival-muted">
                        {c.source === "NO_POLICY_RECORDED" ? "No policy recorded yet"
                          : `${c.source === "OUTLET_POLICY" ? "This outlet" : "Organisation default"} · effective ${fmtDay(c.effective_from)}`}
                        {c.beneficiary_ref ? ` · credit to ${c.beneficiary_ref}` : ""}
                      </p>
                    </div>
                    {d.can_edit && open !== c.component_kind && (
                      <button type="button" onClick={() => { setOpen(c.component_kind); setErr(null); }} data-testid={`policy-change-${c.component_kind}`}
                        className="min-h-[40px] px-3 rounded-md border border-[#E4E0D8] text-[13px] font-medium text-archival-primary">Change</button>
                    )}
                  </div>
                  {open === c.component_kind && <Editor c={c} busy={busy} onSave={save} onCancel={() => setOpen(null)} />}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[12px] text-archival-muted flex items-start gap-1.5" data-testid="policy-footnote">
              <ShieldCheck size={13} className="mt-0.5 shrink-0" />
              Every change is effective-dated and audited. Bills already confirmed keep the policy that applied on their own bill date.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
